import {
	Fragment,
	ReactNode,
	SyntheticEvent,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useParams } from "react-router";
import OrderDetailsHeader from "components/OrderDetailsHeader/OrderDetailsHeader";
import OrderDetailsSummary from "components/OrderDetailsSummary/OrderDetailsSummary";
import OrderDetailsSearchLineItems from "components/OrderDetailsLineItem/OrderDetailsSearchLineItems";
import { ORDER_DETAIL_CTA_PROBLEMS } from "constants/orderDetailsCTAProblems";
import OrderDetailsOnBackorderAccordion from "components/OrderDetailsLineItem/OrderDetailsOnBackorderAccordion";
import OrderDetailsShipmentAccordion from "components/OrderDetailsLineItem/OrderDetailsShipmentAccordion";
import {
	AcknowledgementType,
	BackorderedCabinetCount,
	checkAcknowledgementEligibility,
	GetBackorderCount,
	getOrderLineItems,
	getSubtotalTitle,
	groupOrders,
	PartsQuantityBackorderedLength
} from "../utils/OrderDetailUtil";
import { useInitOrderDetail } from "hooks/useInitOrderDetail";
import OrderDetailsHeaderSkeleton from "components/Skeletons/OrderDetailsHeaderSkeleton";
import OrderDetailsSummarySkeleton from "components/Skeletons/OrderDetailsSummarySkeleton";
import OrderDetailsLineItemSkeleton from "components/Skeletons/OrderDetailsLineItemSkeleton";
import NoOrderDetailsErrorBanner from "components/NoOrdersErrorBanner/NoOrderDetailsErrorBanner";
import OrderSummaryAPIFailure from "components/OrderSummaryAPIFailure/OrderSummaryAPIFailure";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import LeavePageModal from "components/LeavePageModal/LeavePageModal";
import { cartActions } from "features/reducers/replacementOrder/cart.ts";
import { submittingReplacementOrderActions } from "features/reducers/replacementOrder/submittingReplacementOrder.ts";
import DefaultMessage from "components/Common/DefaultMessage/DefaultMessage";
import {
	AcknowledgementText,
	AreYouSureText,
	HangTightSubtitle,
	HangTightTitle,
	ItemsWillClearText,
	NoContinueOrdering,
	NoLineItemsAvailableText,
	OrderDetailsPriceValidationErrorBody,
	OrderDetailsPriceValidationErrorTitle,
	OrderDetailsText
} from "constants/text";
import { formatDateToYYYYMMDD } from "utils/date";
import {
	AcknowledgementTotalPrice,
	NoOrderDetailsBannerContainer,
	OrderDetailsHeaderWrapper,
	OrderDetailsTitle,
	PageContent,
	UngroupedGridWrapper,
	WarningContainer
} from "./orderDetailPage.styles.ts";
import EstimatedSubtotal from "components/NewOrders/ViewPriceEstimates/EstimatedSubtotal.tsx";
import OrderDetailsLineItemGrid from "components/OrderDetailsLineItem/OrderDetailsLineItemGrid/OrderDetailsLineItemGrid.tsx";
import { models } from "types/api/viewModels.ts";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import OrderDetailsCharges from "components/OrderDetailsLineItem/OrderDetailsCharges.tsx";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import PaintChipInstructions from "components/PaintChipInstructions/PaintChipInstructions.tsx";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { getLegalContent } from "utils/legal.tsx";
import WarningBox from "components/Common/WarningBox/WarningBox.tsx";
import { convertToTitleCase } from "utils/string.ts";
import { AcknowledgementPriceFormat, OrderStatus, OrderType } from "types/declarations/enums.ts";
import { useGetPermissionsQuery } from "features/api/permissionsApi.ts";
import { useGetAccountsByIdQuery, useGetBillToInfoQuery } from "features/api/accountApi.ts";
import { Page } from "components/Common/Page";

const OrderDetailPage = () => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const { data: permissionsData } = useGetPermissionsQuery();
	const ackPermission = permissionsData?.acknowledgementPriceFormat;
	const { isOrderDetailsPage } = useUserLocation();
	const dispatch = useDispatch();
	const params = useParams();
	const { isLoading, orderDetail, orderId, error } = useInitOrderDetail(params.orderId);
	const [searchValue, setSearchValue] = useState<string | null>(null);
	const replacementCart = useSelector((state: RootState) => state.cart.replacements);
	const isReplacementCartFull = replacementCart && replacementCart.length > 0;
	const [isLeavingReplacementWarningOpen, setIsLeavingReplacementWarningOpen] = useState(isReplacementCartFull);
	const [ctaMessage, setCtaMessage] = useState<ReactNode | string | undefined>();

	const [billTo, setBillTo] = useState<models["BillToViewModel"] | null>(null);
	const { data: accounts } = useGetAccountsByIdQuery(params.orderId ?? "", { skip: !params.orderId });
	const accountId = accounts?.[0]?.accountId;

	const { data: billToInfo } = useGetBillToInfoQuery(
		{ accountId: accountId ?? "", billToId: orderDetail?.billToId ?? "" },
		{ skip: !accountId || !orderDetail?.billToId }
	);
	const isMerchOrder = orderDetail?.orderType === OrderType.MERCH;
	const effectiveAckPermission = isMerchOrder ? AcknowledgementPriceFormat.LIST : ackPermission;
	const pricingBillTo = isMerchOrder && billTo ? { ...billTo, cashInAdvance: false } : billTo;
	const effectiveCashInAdvance = pricingBillTo?.cashInAdvance ?? false;

	useEffect(() => {
		if (billToInfo) {
			setBillTo(billToInfo);
		}
	}, [billToInfo]);

	const orderComputedValues = useMemo(() => {
		const hasCTADescription = ORDER_DETAIL_CTA_PROBLEMS.find(
			(problem) => problem.statusReason === orderDetail?.ctaReason
		);
		const lineItems = getOrderLineItems(orderDetail);
		const totalCount = lineItems.length;
		const todaysDate = new Date();
		const todayFormattedDate = formatDateToYYYYMMDD(todaysDate);
		const replacementOrderDate = orderDetail?.orderDate?.split("T")[0];
		const partsOnBackorder = PartsQuantityBackorderedLength(lineItems);
		const cabinetsOnBackorder = BackorderedCabinetCount(lineItems);
		const displayBackorderAccordion =
			(partsOnBackorder || cabinetsOnBackorder) && orderDetail?.orderType !== "BACKORDER";

		return {
			hasCTADescription,
			lineItems,
			totalCount,
			todayFormattedDate,
			replacementOrderDate,
			displayBackorderAccordion
		};
	}, [orderDetail]);

	const groupedOrderCache = useRef<models["OrderDetailViewModel"][]>([]);

	const groupedOrders = useMemo(() => {
		const orders = groupOrders(orderDetail, searchValue);

		if (!groupedOrderCache.current.length) {
			groupedOrderCache.current = orders;
		}
		return orders;
	}, [orderDetail, searchValue]);

	const handleOrderSearch = (_event: SyntheticEvent, values: string | null) => {
		setSearchValue(values);
	};

	const closeLeavingReplacementWarning = () => {
		dispatch(cartActions.resetCart());
		dispatch(submittingReplacementOrderActions.resetOrder());
		setIsLeavingReplacementWarningOpen(false);
	};

	useEffect(() => {
		dispatch(newOrderActions.clearNewOrder());
	}, [dispatch]);

	useEffect(() => {
		const ctaDescription = ORDER_DETAIL_CTA_PROBLEMS.find(
			(status) => status.statusReason === orderDetail?.ctaReason
		)?.description;
		const ctaContainsEmail = ctaDescription?.includes("@");
		if (ctaDescription && ctaContainsEmail) {
			const parts = ctaDescription.split("dealercare@cabinetworksgroup.com");
			const dynamicCtaMessage = (
				<>
					{parts[0]}
					<a href={`mailto:${orderDetail.ctaContact}`}>{orderDetail.ctaContact}</a>
					{parts[1]}
				</>
			);
			setCtaMessage(dynamicCtaMessage);
		} else {
			setCtaMessage(ctaDescription);
		}
	}, [orderDetail?.ctaReason, orderDetail?.ctaContact]);

	const renderOrderShipment = useCallback(
		(order: models["OrderDetailViewModel"] | null | undefined) => {
			const originalOrderIndex = groupedOrderCache.current.findIndex(
				(groupedOrder) =>
					groupedOrder.shippingDetails?.trackingNumber === order?.shippingDetails?.trackingNumber
			);
			const originalOrder = groupedOrderCache.current[originalOrderIndex];

			return (
				<OrderDetailsShipmentAccordion
					originalOrder={originalOrder}
					order={order}
					searchValue={searchValue}
					shipmentCount={originalOrderIndex + 1}
					billTo={billTo}
				/>
			);
		},
		[searchValue, billTo]
	);

	const renderErrorState = () => {
		if ((error as any)?.status) {
			return (
				<NoOrderDetailsBannerContainer data-testid="order-detail-page">
					<OrderSummaryAPIFailure statusCode={(error as any)?.status} />
				</NoOrderDetailsBannerContainer>
			);
		}

		if (orderId && orderId !== orderDetail?.orderId && error) {
			return (
				<NoOrderDetailsBannerContainer data-testid="order-detail-page-error-banner">
					<NoOrderDetailsErrorBanner />
				</NoOrderDetailsBannerContainer>
			);
		}

		return null;
	};

	const renderLoadingState = () => (
		<div data-testid="order-detail-page-skeleton">
			<OrderDetailsHeaderSkeleton />
			<OrderDetailsSummarySkeleton />
			{new Array(6).fill(1).map((index) => (
				<OrderDetailsLineItemSkeleton key={index} />
			))}
		</div>
	);

	const { acknowledgementType, isAcknowledgementEligible } = checkAcknowledgementEligibility(
		orderDetail,
		configurationsData,
		effectiveAckPermission
	);
	const effectiveAcknowledgementType = isMerchOrder ? AcknowledgementType.LIST_ACKNOWLEDGEMENT : acknowledgementType;

	const pricingProps = {
		acknowledgementType: effectiveCashInAdvance
			? AcknowledgementType.NET_ACKNOWLEDGEMENT
			: effectiveAcknowledgementType,
		pricing:
			effectiveAckPermission !== AcknowledgementPriceFormat.LIST || effectiveCashInAdvance
				? orderDetail?.pricing?.finalNetPrice
				: orderDetail?.pricing?.listPrice,
		subtotalTitle: getSubtotalTitle(
			orderDetail,
			effectiveAcknowledgementType,
			isOrderDetailsPage,
			effectiveCashInAdvance
		)
	};

	const errorState = renderErrorState();
	if (errorState) return errorState;

	if (isLoading) return renderLoadingState();

	if (orderId !== undefined) {
		sessionStorage.setItem("orderDetailsId", orderId);
	}

	if (orderDetail === undefined) {
		return null;
	}

	const {
		hasCTADescription,
		lineItems,
		totalCount,
		todayFormattedDate,
		replacementOrderDate,
		displayBackorderAccordion
	} = orderComputedValues;

	return (
		<Page
			variant="orders"
			data-testid="order-detail-page"
		>
			{isReplacementCartFull && (
				<LeavePageModal
					url="/"
					open={isLeavingReplacementWarningOpen}
					continueOrdering={() => {}}
					closeModal={closeLeavingReplacementWarning}
					isOnOrderDetailsPage={true}
					orderDetailId={orderId}
					headerText={AreYouSureText}
					subHeaderText={ItemsWillClearText}
					continueButtonText={NoContinueOrdering}
				/>
			)}
			<OrderDetailsHeaderWrapper variant="panel">
				<OrderDetailsHeader
					orderDetail={orderDetail}
					groupedOrders={groupedOrders}
				/>
				{orderDetail.showPaintChipInstructions && <PaintChipInstructions />}
				{orderDetail.orderType !== OrderType.REPLACEMENT &&
					orderDetail.legalNotice &&
					orderDetail.legalNotice.warningTitle && (
						<WarningContainer>
							<WarningBox
								warning={orderDetail.legalNotice.warningTitle}
								message={
									getLegalContent(
										orderDetail.legalNotice.content,
										orderDetail.legalNotice.placeholders
									) ?? []
								}
							/>
						</WarningContainer>
					)}
				{orderDetail.status === OrderStatus.SUBMITTED_WITH_ERRORS && (
					<WarningContainer>
						<WarningBox
							warning={OrderDetailsPriceValidationErrorTitle}
							message={OrderDetailsPriceValidationErrorBody}
						/>
					</WarningContainer>
				)}
				{orderDetail?.ctaReason && hasCTADescription && (
					<WarningContainer>
						<WarningBox
							warning={convertToTitleCase(orderDetail.ctaReason)}
							message={ctaMessage}
							isInfo={orderDetail?.ctaReason === "DELIVERY_DATE_CHANGED"}
						/>
					</WarningContainer>
				)}
			</OrderDetailsHeaderWrapper>
			<OrderDetailsTitle>{isAcknowledgementEligible ? AcknowledgementText : OrderDetailsText}</OrderDetailsTitle>
			<OrderDetailsSummary
				orderDetail={orderDetail}
				billTo={pricingBillTo}
			/>

			<PageContent>
				{Number(displayBackorderAccordion) > 0 && (
					<OrderDetailsOnBackorderAccordion
						orderDetail={orderDetail}
						lineItemsOnBackorderCount={GetBackorderCount(lineItems)}
					/>
				)}

				{totalCount > 0 && (
					<OrderDetailsSearchLineItems
						groupAllByNoneTrackingNumber={lineItems}
						handleOrderSearch={handleOrderSearch}
						searchValue={searchValue}
						lineItemAndShipmentCount={totalCount}
					/>
				)}

				{totalCount === 0 && todayFormattedDate === replacementOrderDate && (
					<DefaultMessage
						dataTestId="default-message-for-no-line-items"
						asset="/assets/HangTight.svg"
						altMessage={NoLineItemsAvailableText}
						title={HangTightTitle}
						subtitle={HangTightSubtitle}
					/>
				)}

				{groupedOrders.map((order, index) => (
					<Fragment key={`${order.orderId}${index}`}>
						{order.configurations && order.configurations.length > 0 && (
							<div>
								{order.shippingDetails?.trackingNumber ? (
									renderOrderShipment(order)
								) : (
									<UngroupedGridWrapper>
										<OrderDetailsLineItemGrid
											orderDetail={order}
											variant="detail"
											billTo={pricingBillTo}
										/>
									</UngroupedGridWrapper>
								)}
							</div>
						)}
					</Fragment>
				))}
				<AcknowledgementTotalPrice
					isListPrice={effectiveAckPermission === AcknowledgementPriceFormat.LIST && !effectiveCashInAdvance}
				>
					<OrderDetailsCharges
						orderDetail={orderDetail}
						ackPermission={effectiveAckPermission}
						billTo={pricingBillTo}
					/>
					<EstimatedSubtotal
						{...pricingProps}
						isReplacement={orderDetail.orderType === OrderType.REPLACEMENT}
					/>
				</AcknowledgementTotalPrice>
			</PageContent>
		</Page>
	);
};

export default OrderDetailPage;
